'use client';

import { useState, useEffect } from 'react';
import { ShieldAlert, LogOut } from 'lucide-react';
import { httpClient } from '@/api';

export const ADMIN_IMPERSONATOR_TOKEN_KEY = 'medikei_admin_impersonator_token';
export const ADMIN_IMPERSONATOR_USER_KEY = 'medikei_admin_impersonator_user';

interface ImpersonationBannerProps {
  doctorName?: string | null;
  crm?: string | null;
  crmState?: string | null;
}

export function ImpersonationBanner({ doctorName, crm, crmState }: ImpersonationBannerProps) {
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [adminUser, setAdminUser] = useState<{ email?: string; name?: string } | null>(null);

  useEffect(() => {
    const backupToken = localStorage.getItem(ADMIN_IMPERSONATOR_TOKEN_KEY);
    const backupUserStr = localStorage.getItem(ADMIN_IMPERSONATOR_USER_KEY);
    if (backupToken) {
      setIsImpersonating(true);
      if (backupUserStr) {
        try {
          setAdminUser(JSON.parse(backupUserStr));
        } catch {
          // ignore parse error
        }
      }
    } else {
      setIsImpersonating(false);
    }
  }, []);

  const handleExitImpersonation = () => {
    const backupToken = localStorage.getItem(ADMIN_IMPERSONATOR_TOKEN_KEY);
    if (backupToken) {
      httpClient.saveAccessToken(backupToken);
    }
    localStorage.removeItem(ADMIN_IMPERSONATOR_TOKEN_KEY);
    localStorage.removeItem(ADMIN_IMPERSONATOR_USER_KEY);

    // Redireciona de volta para a gestão de médicos no Admin
    window.location.href = '/admin/medicos';
  };

  if (!isImpersonating) return null;

  return (
    <div className="bg-gradient-to-r from-amber-600 via-orange-600 to-amber-700 text-white px-4 py-2.5 text-xs font-semibold flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 shadow-md z-[99999] shrink-0 sticky top-0 border-b border-amber-500/40 animate-in fade-in slide-in-from-top-2 duration-200">
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="p-1.5 rounded-lg bg-black/20 text-amber-200 shrink-0">
          <ShieldAlert className="w-4 h-4 animate-pulse" />
        </div>
        <div className="min-w-0 truncate">
          <span className="font-extrabold uppercase tracking-wider text-amber-200 mr-1.5">
            Modo Administrador:
          </span>
          <span className="text-white">
            Acessando a conta de <strong>{doctorName || 'Médico'}</strong>
            {crm && <span className="opacity-90 font-mono ml-1">({crm}/{crmState || 'SP'})</span>}
          </span>
          {adminUser?.email && (
            <span className="text-[11px] text-amber-100/80 ml-2 hidden md:inline-block">
              (Sessão original: {adminUser.email})
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0 self-end sm:self-auto">
        <button
          type="button"
          onClick={handleExitImpersonation}
          className="px-3 py-1.5 bg-white hover:bg-amber-50 text-neutral-900 rounded-xl font-bold text-xs transition-all shadow-xs flex items-center gap-1.5 cursor-pointer hover:scale-[1.02] active:scale-95"
          title="Encerrar sessão personificada e retornar ao painel do administrador"
        >
          <LogOut className="w-3.5 h-3.5 text-amber-700" />
          <span>Voltar para o Painel Admin</span>
        </button>
      </div>
    </div>
  );
}
